import React from 'react';
import type { FieldChange, FieldComment } from '@discovr/types';
import { useCampaignCapabilitiesStore } from '@discovr/features';
import { toast } from 'sonner';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { Textarea } from '../../components/ui/textarea';
import { ChatCircleText, CaretLeft, CaretRight, Check, X, PaperPlaneRight } from '../../design-system/icons';
import { FIELD_LABELS, formatCommentTimestamp, isAdminRole, isSelfApprovedByAdmin } from './editRequestFormat';
import { RoleBadge } from './RoleBadge';
import { AuthorAvatar } from './campaignDetailsParts';
import { useFieldReviewDecision } from './useFieldReviewDecision';
import { DiffValue } from './editRequestDiff';

interface CommentThreadPopoverProps {
  requestId: string;
  fieldPath: string;
  comments: FieldComment[];
  status: FieldChange['status'];
  /** Every OTHER resolved FieldChange for this field path, across every past
   *  CampaignEditRequest, already sorted newest-first by the caller. When
   *  non-empty, a "Change X/N" pager appears above the comment thread,
   *  letting the viewer step back through history — page 1 is always the
   *  live `comments`/`status` this popover was opened for; pages 2..N+1 are
   *  read from `history` in order. See spec requirement #6. */
  history?: FieldChange[];
  /** Struck-through old→new shown at the top of each history page (and, if
   *  provided, the current page too) — omit for callers that don't have a
   *  natural old/new to show (keeps this component usable exactly as before
   *  when unset). */
  oldValue?: unknown;
  newValue?: unknown;
  /** Who submitted the LIVE (page 1) change — an admin-submitted approved
   *  change never had a review conversation to close (admin edits are
   *  self-approved, see `isAdminRole`), so it stays open for comments even
   *  once approved. History pages use their own entry's `submitted_by_role`
   *  instead, since each past change may have a different submitter. */
  submittedByRole?: string;
}

const STATUS_LABEL: Record<FieldChange['status'], string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};
const STATUS_STYLE: Record<FieldChange['status'], string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

/** Persistent per-field comment indicator — a 24px "teardrop" outline (three
 *  rounded corners, one square) matching Figma node 3348-83788. Empty state
 *  shows a muted chat glyph; once someone's posted, the last commenter's
 *  gradient-avatar initial sits inside the same outline instead of an icon.
 *  Either brand or admin can post here at any FieldChange status; the thread
 *  is shared, not author-gated. When the viewer is admin and the CURRENTLY
 *  DISPLAYED page is still pending, icon-only Reject/Approve sit next to the
 *  field name in the header — reject posts whatever's in the textarea (or
 *  nothing — the comment is optional, see spec requirement #7) as the next
 *  comment, same as approve's own optional-comment behavior. Post is a send
 *  button embedded in the textarea itself, chat-composer style. Once a
 *  brand-submitted page is Approved, commenting locks for everyone (both
 *  sides) — a Rejected page stays open for further comment (spec requirement
 *  #8). An admin-submitted page never locks even once approved: admin edits
 *  are self-approved in the same transaction (no review conversation ever
 *  closed), so there's always something to discuss (see `isAdminRole`).
 *  Paging via `history` (requirement #6) only
 *  changes which page's read-only content + comment-lock state is shown;
 *  Approve/Reject/Post always act on THIS popover's own `requestId`/
 *  `fieldPath` (the live one), never a history page's — history pages other
 *  than page 1 are never pending, so canDecide is naturally false there.
 *  Reused by the admin's always-on Details overlay, the brand's
 *  Changes-history view, and the review side panel. */
export function CommentThreadPopover({
  requestId,
  fieldPath,
  comments,
  status,
  history = [],
  oldValue,
  newValue,
  submittedByRole,
}: CommentThreadPopoverProps) {
  const { addFieldComment } = useCampaignCapabilitiesStore();
  const { approve, reject, deciding, canDecide: reviewerCanDecide, invalidate } = useFieldReviewDecision(requestId, fieldPath);
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [posting, setPosting] = React.useState(false);
  const [pageIndex, setPageIndex] = React.useState(0);

  if (!addFieldComment) return null;

  const label = FIELD_LABELS[fieldPath] ?? fieldPath;
  const pageCount = 1 + history.length;

  const currentPage: {
    comments: FieldComment[];
    status: FieldChange['status'];
    oldValue?: unknown;
    newValue?: unknown;
    submittedByRole?: string;
  } =
    pageIndex === 0
      ? { comments, status, oldValue, newValue, submittedByRole }
      : {
          comments: history[pageIndex - 1]?.comments ?? [],
          status: history[pageIndex - 1]?.status ?? 'approved',
          oldValue: history[pageIndex - 1]?.old_value,
          newValue: history[pageIndex - 1]?.new_value,
          submittedByRole: history[pageIndex - 1]?.submitted_by_role,
        };

  const hasComments = comments.length > 0;
  const lastAuthor = hasComments ? (comments[comments.length - 1]?.author_name ?? '?') : '';
  // Once a field has been decided (approved/rejected) there's always
  // something to review even with zero comments (the old→new decision
  // itself), so the trigger reads "View comments" rather than "Add a
  // comment" — only a still-pending, comment-free field invites a first one.
  const isViewOnlyTrigger = hasComments || status !== 'pending';
  // canDecide (and the comment box's lock) always reflect the LIVE page
  // (pageIndex 0) status — Approve/Reject only ever act on the live entry,
  // and locking is a property of whichever page is on screen right now.
  const canDecide = reviewerCanDecide && pageIndex === 0 && status === 'pending';
  const commentsLocked = currentPage.status === 'approved' && !isAdminRole(currentPage.submittedByRole);
  const busy = posting || deciding;

  const handlePost = async () => {
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    try {
      await addFieldComment?.(requestId, fieldPath, text);
      invalidate();
      setDraft('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const handleApprove = async () => {
    try {
      await approve(draft.trim() || undefined);
      setDraft('');
    } catch {
      // useFieldReviewDecision already surfaced a toast — nothing else to do.
    }
  };

  const handleReject = async () => {
    try {
      // Reject comment is optional (spec requirement #7 — no dedicated
      // "reason" field, just whatever's in the box, which may be empty).
      await reject(draft.trim());
      setDraft('');
    } catch {
      // useFieldReviewDecision already surfaced a toast — nothing else to do.
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        // Reset to the live page whenever the popover closes so reopening
        // it never surprises the viewer with a stale history page.
        if (!v) setPageIndex(0);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={isViewOnlyTrigger ? `View comments on ${label}` : `Add a comment on ${label}`}
          className="flex size-6 shrink-0 items-center justify-center rounded-tl-full rounded-tr-full rounded-br-full border-[0.6px] border-[#d4d4d4] bg-[#fafafa] transition-transform hover:scale-110"
        >
          {hasComments ? (
            <AuthorAvatar name={lastAuthor} size="sm" />
          ) : (
            <ChatCircleText size={13} className="text-muted-foreground" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3" align="start">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 truncate text-body-sm-medium text-foreground">{label}</div>
          {canDecide && (
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                title="Reject"
                aria-label="Reject"
                onClick={handleReject}
                disabled={busy}
                className="flex size-6 items-center justify-center rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200 disabled:opacity-40"
              >
                <X size={13} weight="bold" />
              </button>
              <button
                type="button"
                title="Approve"
                aria-label="Approve"
                onClick={handleApprove}
                disabled={busy}
                className="flex size-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-40"
              >
                <Check size={13} weight="bold" />
              </button>
            </div>
          )}
        </div>

        {pageCount > 1 && (
          <div className="flex items-center gap-1 text-caption-regular text-muted-foreground">
            <button
              type="button"
              aria-label="Previous change"
              disabled={pageIndex === 0}
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              className="flex size-5 items-center justify-center rounded disabled:opacity-30"
            >
              <CaretLeft size={12} weight="bold" />
            </button>
            <span>
              Change {pageIndex + 1}/{pageCount}
            </span>
            <button
              type="button"
              aria-label="Next change"
              disabled={pageIndex === pageCount - 1}
              onClick={() => setPageIndex((i) => Math.min(pageCount - 1, i + 1))}
              className="flex size-5 items-center justify-center rounded disabled:opacity-30"
            >
              <CaretRight size={12} weight="bold" />
            </button>
          </div>
        )}

        {/* The live page's value is already visible on the field's own
            card/form right where this popover was opened from — repeating
            it here was redundant. History pages (a past resolved change)
            aren't shown anywhere else, so those keep a compact value line,
            reusing DiffValue's added/removed coloring for consistency with
            the rest of the diff UI. */}
        {pageIndex !== 0 && (currentPage.oldValue !== undefined || currentPage.newValue !== undefined) && (
          <div className="flex items-center justify-between gap-2 rounded-md bg-muted px-2.5 py-2 text-body-sm-regular">
            <DiffValue path={fieldPath} oldValue={currentPage.oldValue} newValue={currentPage.newValue} />
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-caption-medium uppercase ${STATUS_STYLE[currentPage.status]}`}>
              {isSelfApprovedByAdmin({ status: currentPage.status, submitted_by_role: currentPage.submittedByRole })
                ? 'Applied by admin'
                : STATUS_LABEL[currentPage.status]}
            </span>
          </div>
        )}

        {currentPage.comments.length > 0 ? (
          <div className="max-h-64 space-y-3 overflow-auto">
            {currentPage.comments.map((c, i) => (
              <div key={i} className="flex items-start gap-2">
                <AuthorAvatar name={c.author_name} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-caption-regular text-muted-foreground">
                    <span className="max-w-[150px] truncate font-medium text-foreground">{c.author_name ?? 'Unknown'}</span>
                    <RoleBadge role={c.author_role} />
                    <span>· {formatCommentTimestamp(c.created_at)}</span>
                  </div>
                  <p className="text-body-sm-regular text-foreground">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-caption-regular text-muted-foreground italic">No comments yet.</p>
        )}

        {pageIndex !== 0 ? (
          <p className="border-t border-border pt-3 text-caption-regular text-muted-foreground italic">
            Past change — read-only.
          </p>
        ) : commentsLocked ? (
          <p className="border-t border-border pt-3 text-caption-regular text-muted-foreground italic">
            This change is approved — commenting is closed.
          </p>
        ) : (
          <div className="relative border-t border-border pt-3">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write a comment…"
              rows={2}
              className="field-sizing-fixed min-h-0 resize-none pr-9"
              autoFocus
            />
            <button
              type="button"
              aria-label="Post comment"
              title="Post comment"
              onClick={handlePost}
              disabled={!draft.trim() || busy}
              className="absolute right-1.5 bottom-1.5 flex size-6 items-center justify-center rounded-full bg-foreground text-background transition-opacity disabled:opacity-30"
            >
              <PaperPlaneRight size={13} weight="bold" />
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
